import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaginationBar, usePagination } from "@/components/Pagination";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Save, X, Search, Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

// Which filters apply to which master tab
const TAB_FILTERS = {
  items:      ["company", "category", "group"],
  companies:  ["category", "group"],
  categories: ["company", "group"],
  groups:     ["category"],
  sectors:    [],
  hsn_codes:  ["category"],
};
const TAB_ORDER = ["items", "companies", "categories", "groups", "sectors", "hsn_codes"];

export function FilterCombobox({ label, value, options, onChange, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground capitalize">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" data-testid={testid}
            className="mt-1 w-44 justify-between font-normal">
            <span className="truncate">{value === "all" ? "All" : value}</span>
            <ChevronsUpDown className="w-3.5 h-3.5 ml-1 shrink-0 opacity-50"/>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-44 p-0" align="start">
          <Command>
            <CommandInput placeholder={`Search ${label}…`} data-testid={`${testid}-search`}/>
            <CommandList>
              <CommandEmpty>No matches</CommandEmpty>
              <CommandGroup>
                <CommandItem value="__all__" onSelect={() => { onChange("all"); setOpen(false); }} data-testid={`${testid}-opt-all`}>
                  <Check className={`w-3.5 h-3.5 mr-1 ${value === "all" ? "opacity-100" : "opacity-0"}`}/> All
                </CommandItem>
                {options.map(o => (
                  <CommandItem key={o} value={o} onSelect={() => { onChange(o); setOpen(false); }} data-testid={`${testid}-opt-${o}`}>
                    <Check className={`w-3.5 h-3.5 mr-1 ${value === o ? "opacity-100" : "opacity-0"}`}/> {o}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function ManageMasters() {
  const { bid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState({}); // { master: { recordId: bool } }
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("items");
  const [filters, setFilters] = useState({});

  const load = async () => {
    try {
      const { data: res } = await api.get(`/admin/establishments/${bid}/master-auth`);
      setData(res);
      const c = {};
      res.masters.forEach(m => {
        c[m.key] = {};
        m.records.forEach(r => { c[m.key][r.id] = r.active; });
      });
      setChecked(c);
      setDirty(false);
    } catch (e) { toast.error(formatErr(e)); nav("/admin/master-auth"); }
  };
  useEffect(() => { load(); }, [bid]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (master, field, value) =>
    setFilters(f => ({ ...f, [master]: { ...f[master], [field]: value } }));

  const activeMaster = useMemo(() => (data?.masters || []).find(m => m.key === tab), [data, tab]);
  const activeFilters = TAB_FILTERS[tab] || [];

  // Build per-tab filter option lists from the master's own records
  const filterOptions = (field) => {
    if (!activeMaster) return [];
    const set = new Set();
    activeMaster.records.forEach(r => { if (r[field]) set.add(r[field]); });
    return [...set].sort();
  };

  const visibleRecords = useMemo(() => {
    if (!activeMaster) return [];
    const f = filters[tab] || {};
    const q = (f.search || "").toLowerCase();
    return activeMaster.records.filter(r => {
      if (q && !(r.label || "").toLowerCase().includes(q)) return false;
      return activeFilters.every(fld => {
        const v = f[fld];
        return !v || v === "all" || r[fld] === v;
      });
    });
  }, [activeMaster, tab, filters, activeFilters]);

  const pg = usePagination(visibleRecords, JSON.stringify([tab, filters]));

  const toggle = (master, rid, v) => {
    setChecked(c => ({ ...c, [master]: { ...c[master], [rid]: v } }));
    setDirty(true);
  };
  const bulkSet = (ids, v) => {
    setChecked(c => {
      const next = { ...c[tab] };
      ids.forEach(id => { next[id] = v; });
      return { ...c, [tab]: next };
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const masters = {};
      TAB_ORDER.forEach(mkey => {
        masters[mkey] = Object.entries(checked[mkey] || {}).filter(([, v]) => v).map(([id]) => id);
      });
      await api.put(`/admin/establishments/${bid}/master-auth`, { masters });
      toast.success("Master authorisation saved");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };
  const cancel = () => { load(); toast.info("Changes discarded"); };

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const enabledCount = (m) => Object.values(checked[m.key] || {}).filter(Boolean).length;
  const visibleIds = visibleRecords.map(r => r.id);
  const allVisibleOn = visibleIds.length && visibleIds.every(id => checked[tab]?.[id]);

  return (
    <div className="p-6 space-y-5 max-w-6xl" data-testid="mm-page">
      <Link to="/admin/master-auth" data-testid="mm-back"
        className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary">
        <ArrowLeft className="w-3 h-3"/> Back to Master Authorisation
      </Link>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="text-2xl">{data.establishment.name}</CardTitle>
            <CardDescription>Authorise shared master data this establishment can access.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={cancel} disabled={!dirty || saving} data-testid="mm-cancel">
              <X className="w-4 h-4 mr-1"/> Cancel
            </Button>
            <Button onClick={save} disabled={!dirty || saving} data-testid="mm-save">
              <Save className="w-4 h-4 mr-1"/> {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab} data-testid="mm-tabs">
            <TabsList className="flex-wrap h-auto">
              {data.masters.map(m => (
                <TabsTrigger key={m.key} value={m.key} data-testid={`mm-tab-${m.key}`}>
                  {m.label.replace(" Master", "")}
                  <Badge variant="secondary" className="ml-2" data-testid={`mm-tab-count-${m.key}`}>
                    {enabledCount(m)}/{m.records.length}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>

            {data.masters.map(m => (
              <TabsContent key={m.key} value={m.key} className="mt-4" data-testid={`mm-tabpanel-${m.key}`}>
                <div className="flex gap-3 items-end flex-wrap mb-3">
                  <div className="min-w-52">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Search</Label>
                    <div className="relative mt-1">
                      <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground"/>
                      <Input data-testid={`mm-search-${m.key}`} value={(filters[m.key] || {}).search || ""}
                        onChange={(e) => setFilter(m.key, "search", e.target.value)}
                        placeholder="Search records by name…" className="pl-8"/>
                    </div>
                  </div>
                  {(TAB_FILTERS[m.key] || []).map(fld => (
                    <FilterCombobox key={fld} label={fld} value={(filters[m.key] || {})[fld] || "all"}
                      options={filterOptions(fld)} onChange={(v) => setFilter(m.key, fld, v)}
                      testid={`mm-filter-${m.key}-${fld}`}/>
                  ))}
                  <div className="ml-auto flex gap-2">
                    <Button size="sm" variant="outline" data-testid={`mm-select-all-${m.key}`}
                      onClick={() => bulkSet(visibleIds, true)}>Select All</Button>
                    <Button size="sm" variant="outline" data-testid={`mm-deselect-all-${m.key}`}
                      onClick={() => bulkSet(visibleIds, false)}>Deselect All</Button>
                  </div>
                </div>
                <div className="border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Record</TableHead>
                        {(TAB_FILTERS[m.key] || []).map(fld =>
                          <TableHead key={fld} className="capitalize">{fld}</TableHead>)}
                        <TableHead className="text-right w-32">Enable / Disable</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {m.key === tab && pg.pageItems.map(r => (
                        <TableRow key={r.id} data-testid={`mm-row-${m.key}-${r.id}`}>
                          <TableCell className="font-medium">{r.label}</TableCell>
                          {(TAB_FILTERS[m.key] || []).map(fld =>
                            <TableCell key={fld} className="text-xs text-muted-foreground">{r[fld] || "—"}</TableCell>)}
                          <TableCell className="text-right">
                            <Switch data-testid={`mm-toggle-${m.key}-${r.id}`} checked={!!checked[m.key]?.[r.id]}
                              onCheckedChange={(v) => toggle(m.key, r.id, v)}/>
                          </TableCell>
                        </TableRow>
                      ))}
                      {m.key === tab && !visibleRecords.length && (
                        <TableRow><TableCell colSpan={2 + (TAB_FILTERS[m.key] || []).length}
                          className="text-center text-muted-foreground py-8" data-testid={`mm-empty-${m.key}`}>
                          {m.records.length ? "No records match the current filters." : "No records in this master"}
                        </TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <PaginationBar pg={pg}/>
                </div>
                <div className="text-xs text-muted-foreground mt-2" data-testid={`mm-visible-info-${m.key}`}>
                  Showing {visibleRecords.length} of {m.records.length} · {allVisibleOn && visibleIds.length ? "all visible enabled" : "mixed"}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
